import axiosHttp from "services/axiosHttp";

const { createSlice, createAsyncThunk } = require("@reduxjs/toolkit");

const buildQuery = (params) => {
	const search = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value === undefined || value === null || value === "") {
			return;
		}
		search.set(key, String(value));
	});
	return search.toString();
};

const normalizeListResponse = (res) => {
	const body = res?.data;
	const root = body?.data !== undefined ? body.data : body;
	const docs = root?.docs ?? [];
	const total = Number(root?.total ?? 0);
	const page = Number(root?.page ?? 1);
	const limit = Number(root?.limit ?? 20);
	return { docs, total, page, limit, rawStatus: root?.status };
};

const normalizeFiltersResponse = (res) => {
	const body = res?.data;
	const root = body?.data !== undefined ? body.data : body;
	return {
		events: Array.isArray(root?.events) ? root.events : [],
		departments: Array.isArray(root?.departments) ? root.departments : [],
		submitters: Array.isArray(root?.submitters) ? root.submitters : [],
		categories: Array.isArray(root?.categories) ? root.categories : [],
		statuses: Array.isArray(root?.statuses) ? root.statuses : [],
	};
};

export const fetchEventSubmissionBackoffice = createAsyncThunk(
	"fetchEventSubmissionBackoffice",
	async (query, { rejectWithValue }) => {
		try {
			const qs = buildQuery(query);
			const response = await axiosHttp.get(`event-submission/submissions/backoffice?${qs}`);
			return normalizeListResponse(response);
		} catch (err) {
			const status = err?.response?.status;
			const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Request failed";
			return rejectWithValue({ status, message, data: err?.response?.data });
		}
	},
);

export const fetchEventSubmissionBackofficeFilters = createAsyncThunk(
	"fetchEventSubmissionBackofficeFilters",
	async ({ companyId } = {}, { rejectWithValue }) => {
		try {
			const search = new URLSearchParams();
			if (companyId !== undefined && companyId !== null && companyId !== "") {
				search.set("companyId", String(companyId));
			}
			const qs = search.toString();
			const url = qs ? `event-submission/submissions/backoffice/filters?${qs}` : "event-submission/submissions/backoffice/filters";
			const response = await axiosHttp.get(url);
			return normalizeFiltersResponse(response);
		} catch (err) {
			const status = err?.response?.status;
			const message = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Failed to load filters";
			return rejectWithValue({ status, message });
		}
	},
);

const emptyFilterOptions = () => ({
	events: [],
	departments: [],
	submitters: [],
	categories: [],
	statuses: [],
});

const eventSubmissionBackofficeSlice = createSlice({
	name: "eventSubmissionBackoffice",
	initialState: {
		isLoading: false,
		docs: [],
		total: 0,
		page: 1,
		limit: 20,
		error: null,
		httpStatus: null,
		filterOptions: null,
		filtersLoading: false,
		filtersError: null,
	},

	reducers: {
		clearEventSubmissionBackofficeError: (state) => {
			state.error = null;
			state.httpStatus = null;
		},
		resetEventSubmissionBackoffice: (state) => {
			state.isLoading = false;
			state.docs = [];
			state.total = 0;
			state.error = null;
			state.httpStatus = null;
			state.filterOptions = null;
			state.filtersLoading = false;
			state.filtersError = null;
		},
	},

	extraReducers: (builder) => {
		builder.addCase(fetchEventSubmissionBackoffice.pending, (state) => {
			state.isLoading = true;
			state.error = null;
			state.httpStatus = null;
		});
		builder.addCase(fetchEventSubmissionBackoffice.fulfilled, (state, action) => {
			state.isLoading = false;
			state.docs = action.payload.docs;
			state.total = action.payload.total;
			state.page = action.payload.page;
			state.limit = action.payload.limit;
		});
		builder.addCase(fetchEventSubmissionBackoffice.rejected, (state, action) => {
			state.isLoading = false;
			state.docs = [];
			state.error = action.payload?.message || action.error?.message || "Failed to load submissions";
			state.httpStatus = action.payload?.status ?? null;
		});

		builder.addCase(fetchEventSubmissionBackofficeFilters.pending, (state) => {
			state.filtersLoading = true;
			state.filtersError = null;
		});
		builder.addCase(fetchEventSubmissionBackofficeFilters.fulfilled, (state, action) => {
			state.filtersLoading = false;
			state.filterOptions = action.payload;
			state.filtersError = null;
		});
		builder.addCase(fetchEventSubmissionBackofficeFilters.rejected, (state, action) => {
			state.filtersLoading = false;
			state.filterOptions = emptyFilterOptions();
			state.filtersError = action.payload?.message || action.error?.message || "Failed to load filters";
		});
	},
});

export const { clearEventSubmissionBackofficeError, resetEventSubmissionBackoffice } = eventSubmissionBackofficeSlice.actions;
export default eventSubmissionBackofficeSlice.reducer;
